import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Heart, 
  MapPin, 
  EnvelopeSimple, 
  ChatCircleDots,
  PaperPlaneTilt,
  X,
  CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([
    { 
      id: 1, 
      type: 'bot', 
      text: 'Bună ziua! Sunt asistentul virtual MEDICULDIGITAL. Cum vă pot ajuta?' 
    }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to backend
    console.log('Contact form submitted:', formData);
    setFormSubmitted(true);
    toast.success('Mesajul a fost trimis! Vă vom contacta în curând.');
    setFormData({ name: '', email: '', subject: '', message: '' });
    setTimeout(() => setFormSubmitted(false), 3000);
  };

  const handleChatSend = () => {
    if (!chatInput.trim()) return;
    
    const userMessage = { 
      id: Date.now(), 
      type: 'user', 
      text: chatInput 
    };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');

    // Simulate bot response
    setTimeout(() => {
      const botResponses = [
        'Mulțumim pentru mesaj! Un reprezentant vă va contacta în cel mai scurt timp.',
        'Am înregistrat solicitarea dumneavoastră. Veți primi un răspuns pe email.',
        'Pentru urgențe medicale, vă rugăm să apelați 112 sau să folosiți butonul SOS din aplicație.',
        'Echipa noastră de suport va analiza cererea și vă va răspunde în 24-48 de ore.'
      ];
      const randomResponse = botResponses[Math.floor(Math.random() * botResponses.length)];
      
      setChatMessages(prev => [...prev, {
        id: Date.now(),
        type: 'bot',
        text: randomResponse
      }]);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="contact-page">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/dashboard" className="flex items-center gap-2 text-primary">
              <Heart weight="fill" className="w-7 h-7" />
              <span className="text-xl font-bold font-[Manrope]">MEDICULDIGITAL</span>
            </a>
            <a 
              href="/dashboard" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              ← Înapoi la Dashboard
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid gap-12 lg:grid-cols-2">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold font-[Manrope] text-foreground mb-4">
                Contact
              </h1>
              <p className="text-lg text-muted-foreground">
                Suntem aici să vă ajutăm. Contactați-ne pentru orice întrebare sau sugestie.
              </p>
            </div>

            {/* Company Info */}
            <div className="medical-card p-6 space-y-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <MapPin weight="duotone" className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Sediu</h3>
                  <p className="text-muted-foreground">
                    DOCTOR DIGITAL LLC<br />
                    Delaware, United States of America
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <EnvelopeSimple weight="duotone" className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email</h3>
                  <p className="text-muted-foreground">
                    contact@doctordigital.com
                  </p>
                </div>
              </div>
            </div>

            {/* Chatbot Button */}
            <Button
              onClick={() => setChatOpen(true)}
              className="w-full h-14 text-lg gap-3"
              data-testid="open-chatbot-button"
            >
              <ChatCircleDots weight="fill" className="w-6 h-6" />
              Deschide Chat Support
            </Button>
          </div>

          {/* Contact Form */}
          <div className="medical-card p-6">
            <h2 className="text-xl font-semibold font-[Manrope] text-foreground mb-6">
              Trimite un mesaj
            </h2>

            {formSubmitted ? (
              <div className="text-center py-12">
                <CheckCircle weight="fill" className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Mesaj trimis!</h3>
                <p className="text-muted-foreground">Vă vom răspunde în cel mai scurt timp.</p>
              </div>
            ) : (
              <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nume</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="Numele dumneavoastră"
                      value={formData.name}
                      onChange={handleFormChange}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="email@exemplu.com"
                      value={formData.email}
                      onChange={handleFormChange}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="subject">Subiect</Label>
                  <Input
                    id="subject"
                    name="subject"
                    placeholder="Despre ce doriți să ne scrieți?"
                    value={formData.subject}
                    onChange={handleFormChange}
                    required
                    data-testid="contact-subject-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Mesaj</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Scrieți mesajul dumneavoastră aici..."
                    value={formData.message}
                    onChange={handleFormChange}
                    required
                    className="min-h-[150px] resize-none"
                    data-testid="contact-message-input"
                  />
                </div>

                <Button type="submit" className="w-full h-12" data-testid="contact-submit-button">
                  <PaperPlaneTilt weight="bold" className="w-5 h-5 mr-2" />
                  Trimite mesajul
                </Button>
              </form>
            )}
          </div>
        </div>
      </main>

      {/* Chatbot Dialog */}
      <Dialog open={chatOpen} onOpenChange={setChatOpen}>
        <DialogContent className="sm:max-w-md h-[500px] flex flex-col" data-testid="chatbot-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChatCircleDots weight="fill" className="w-5 h-5 text-primary" />
              Chat Support
            </DialogTitle>
            <DialogDescription>
              Asistentul nostru virtual vă stă la dispoziție
            </DialogDescription>
          </DialogHeader>
          
          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto space-y-3 py-4">
            {chatMessages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2 rounded-lg ${
                    msg.type === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="Scrieți mesajul..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleChatSend()}
              data-testid="chatbot-input"
            />
            <Button onClick={handleChatSend} size="icon" data-testid="chatbot-send-button">
              <PaperPlaneTilt weight="fill" className="w-5 h-5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
